import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import type { OrgWideEvent } from '@/api/org';
import { getApiErrorMessage } from '@/api/helpers';
import { MorphingSquare } from '@/components/ui/morphing-square';
import { SlideOver } from '@/components/calendar/panels/SlideOver';
import { hasOrgWideEventPermission } from '@/lib/calendarPermissions';
import { useCalendarPermissions } from '@/hooks/preferences/useCalendarPermissions';
import { useOrgWideEventsQuery } from '@/hooks/calendar/useOrgQueries';
import {
  useCreateOrgWideEventMutation,
  useDeleteOrgWideEventMutation,
  usePatchOrgWideEventMutation,
} from '@/hooks/calendar/useOrgMutations';

export type OrgPanelSection = 'events';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialSection: 'events' | 'holidays' | 'schedule';
  range: { from: string; to: string } | null;
};

export function OrgOverviewPanel({ isOpen, onClose, range }: Props) {
  const perms = useCalendarPermissions();
  const showWideEvents = true;
  const canCreateWideEvent = hasOrgWideEventPermission(perms, 'create');
  const canUpdateWideEvent = hasOrgWideEventPermission(perms, 'update');
  const canDeleteWideEvent = hasOrgWideEventPermission(perms, 'delete');

  const wideQ = useOrgWideEventsQuery(showWideEvents, range ?? undefined);
  const createWideEventMutation = useCreateOrgWideEventMutation();
  const patchWideEventMutation = usePatchOrgWideEventMutation();
  const deleteWideEventMutation = useDeleteOrgWideEventMutation();

  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [editingWideEvent, setEditingWideEvent] = useState<OrgWideEvent | null>(null);
  const [eventTitle, setEventTitle] = useState('');
  const [eventStartsAt, setEventStartsAt] = useState('');
  const [eventEndsAt, setEventEndsAt] = useState('');
  const [eventAllDay, setEventAllDay] = useState(false);
  const [eventVenue, setEventVenue] = useState('');
  const [eventDescription, setEventDescription] = useState('');

  const wideEventMutationError = useMemo(() => {
    if (createWideEventMutation.isError) return getApiErrorMessage(createWideEventMutation.error, 'Could not save company event');
    if (patchWideEventMutation.isError) return getApiErrorMessage(patchWideEventMutation.error, 'Could not save company event');
    if (deleteWideEventMutation.isError) return getApiErrorMessage(deleteWideEventMutation.error, 'Could not delete company event');
    return null;
  }, [
    createWideEventMutation.error,
    createWideEventMutation.isError,
    deleteWideEventMutation.error,
    deleteWideEventMutation.isError,
    patchWideEventMutation.error,
    patchWideEventMutation.isError,
  ]);

  const resetWideEventForm = () => {
    setEditingWideEvent(null);
    setEventTitle('');
    setEventStartsAt('');
    setEventEndsAt('');
    setEventAllDay(false);
    setEventVenue('');
    setEventDescription('');
  };

  const openWideEventCreate = () => {
    resetWideEventForm();
    setEventFormOpen(true);
  };

  const openWideEventEdit = (wideEvent: OrgWideEvent) => {
    setEditingWideEvent(wideEvent);
    setEventTitle(wideEvent.title);
    setEventStartsAt(toDateTimeLocalValue(wideEvent.startsAt));
    setEventEndsAt(toDateTimeLocalValue(wideEvent.endsAt));
    setEventAllDay(wideEvent.allDay);
    setEventVenue(wideEvent.venue ?? '');
    setEventDescription(wideEvent.description ?? '');
    setEventFormOpen(true);
  };

  const onWideEventSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventTitle.trim() || !eventStartsAt || !eventEndsAt) return;
    const input = {
      title: eventTitle.trim(),
      startsAt: new Date(eventStartsAt).toISOString(),
      endsAt: new Date(eventEndsAt).toISOString(),
      allDay: eventAllDay,
      venue: eventVenue.trim() ? eventVenue.trim() : null,
      description: eventDescription.trim() ? eventDescription.trim() : null,
    };
    try {
      if (editingWideEvent) {
        await patchWideEventMutation.mutateAsync({ id: editingWideEvent.id, input });
      } else {
        await createWideEventMutation.mutateAsync(input);
      }
      setEventFormOpen(false);
      resetWideEventForm();
    } catch {
      /* surfaced via mutation state */
    }
  };

  if (!isOpen) return null;

  return (
    <SlideOver onClose={onClose} widthClass="max-w-xl" labelledBy="org-overview-panel-title">
      {(close) => (
        <>
        <header className="sticky top-0 z-10 border-b border-slate-100 bg-white px-8 py-5 dark:border-white/5 dark:bg-card">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 id="org-overview-panel-title" className="text-xl font-bold tracking-tight text-[#0f172b] dark:text-white">Organization Manager</h3>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                Company Events
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
            >
              <CloseIcon />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8 custom-scrollbar">
          <section>
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Company events</h4>
              {showWideEvents && canCreateWideEvent ? (
                <button
                  type="button"
                  onClick={openWideEventCreate}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] px-4 py-2 text-xs font-semibold text-white transition hover:opacity-95 active:scale-95"
                >
                  <span className="text-sm leading-none">+</span>
                  Add company event
                </button>
              ) : null}
            </div>
            {eventFormOpen ? (
              <form onSubmit={onWideEventSubmit} className="mt-4 space-y-3 rounded-3xl border border-slate-100 bg-white p-5 dark:border-white/5 dark:bg-card">
                <input
                  type="text"
                  value={eventTitle}
                  onChange={(e) => setEventTitle(e.target.value)}
                  placeholder="Company event title"
                  className="w-full rounded-2xl border border-slate-200 bg-[#F6F7FB] px-4 py-2.5 text-sm text-[#0f172b] outline-none transition placeholder:text-slate-400 focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10"
                  required
                />
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    type="datetime-local"
                    value={eventStartsAt}
                    onChange={(e) => setEventStartsAt(e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-[#F6F7FB] px-4 py-2.5 text-sm text-[#0f172b] outline-none transition focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10"
                    required
                  />
                  <input
                    type="datetime-local"
                    value={eventEndsAt}
                    onChange={(e) => setEventEndsAt(e.target.value)}
                    className="rounded-2xl border border-slate-200 bg-[#F6F7FB] px-4 py-2.5 text-sm text-[#0f172b] outline-none transition focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10"
                    required
                  />
                </div>
                <label className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
                  <input type="checkbox" checked={eventAllDay} onChange={(e) => setEventAllDay(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-[#5B47E8]" />
                  All day event
                </label>
                <input
                  type="text"
                  value={eventVenue}
                  onChange={(e) => setEventVenue(e.target.value)}
                  placeholder="Venue (optional)"
                  className="w-full rounded-2xl border border-slate-200 bg-[#F6F7FB] px-4 py-2.5 text-sm text-[#0f172b] outline-none transition placeholder:text-slate-400 focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10"
                />
                <textarea
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full rounded-2xl border border-slate-200 bg-[#F6F7FB] px-4 py-2.5 text-sm text-[#0f172b] outline-none transition placeholder:text-slate-400 focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10"
                  rows={2}
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={createWideEventMutation.isPending || patchWideEventMutation.isPending}
                    className="rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] px-5 py-2 text-xs font-semibold text-white transition hover:opacity-95 active:scale-95 disabled:opacity-50"
                  >
                    {createWideEventMutation.isPending || patchWideEventMutation.isPending ? 'Saving...' : editingWideEvent ? 'Update event' : 'Create event'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEventFormOpen(false);
                      resetWideEventForm();
                    }}
                    className="rounded-full bg-[#F3F4F8] px-5 py-2 text-xs font-semibold text-slate-600 transition hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : wideQ.isPending ? (
              <div className="mt-4 flex justify-center py-4"><MorphingSquare className="h-8 w-8" message="Loading..." messagePlacement="right" /></div>
            ) : wideQ.error ? (
              <p className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-600 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-400">Could not load company events.</p>
            ) : (
              <ul className="mt-4 space-y-3 text-sm">
                {(wideQ.data ?? []).length === 0 ? (
                  <li className="rounded-3xl border border-dashed border-[#5B6CF6]/30 bg-white px-6 py-12 text-center text-sm font-semibold text-slate-500 dark:border-[#8B7CFF]/30 dark:bg-card dark:text-slate-400">No company events in this range.</li>
                ) : (
                  wideQ.data?.map((e) => (
                    <li key={e.id} className="rounded-2xl border border-slate-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-[#0f172b] dark:text-white">{e.title}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {e.allDay
                              ? `${format(parseISO(e.startsAt), 'MMM d')} (all day)`
                              : `${format(parseISO(e.startsAt), 'MMM d, p')} - ${format(parseISO(e.endsAt), 'p')}`}
                          </p>
                          {e.venue ? <p className="text-xs text-slate-500 dark:text-slate-400">Venue: {e.venue}</p> : null}
                          {e.description ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{e.description}</p> : null}
                        </div>
                        <div className="flex gap-2">
                          {canUpdateWideEvent ? (
                            <button
                              type="button"
                              onClick={() => openWideEventEdit(e)}
                              className="rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-semibold text-[#5B47E8] transition hover:bg-[#E0E7FF] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]"
                            >
                              Edit
                            </button>
                          ) : null}
                          {canDeleteWideEvent ? (
                            <button
                              type="button"
                              onClick={() => {
                                if (!window.confirm(`Delete company event "${e.title}"?`)) return;
                                deleteWideEventMutation.mutate(e.id);
                              }}
                              className="rounded-full bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-100 dark:bg-rose-500/10 dark:text-rose-400"
                            >
                              Delete
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  ))
                )}
              </ul>
            )}
            {wideEventMutationError ? <p className="mt-2 text-xs text-red-600">{wideEventMutationError}</p> : null}
          </section>
        </div>
        </>
      )}
    </SlideOver>
  );
}

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function toDateTimeLocalValue(dateLike: string): string {
  return format(parseISO(dateLike), "yyyy-MM-dd'T'HH:mm");
}
